import React, { useState } from 'react';
import { Container, Row, Col, Card, ListGroup } from 'react-bootstrap';
import { FiClock, FiMail, FiChevronRight } from 'react-icons/fi';

import { THEME } from '../../utils/constants';

export default function TermsAndConditions() {
    const [activeSection, setActiveSection] = useState('sec-1');

    const customColors = {
        cardBg: '#0d213a',
        borderMuted: 'rgba(255, 255, 255, 0.05)'
    };

    const sections = [
        { id: 'sec-1', title: '1. Corporate Eligibility' },
        { id: 'sec-2', title: '2. Scope of Services' },
        { id: 'sec-3', title: '3. Strategic User Responsibilities' },
        { id: 'sec-4', title: '4. Financial Framework & Fees' },
        { id: 'sec-5', title: '5. Boundaries of Liability' },
        { id: 'sec-6', title: '6. Account Suspension & Termination' },
        { id: 'sec-7', title: '7. Proprietary Intellectual Property' },
        { id: 'sec-8', title: '8. Definite Limitation of Liability' },
        { id: 'sec-9', title: '9. Revisions to Terms' },
        { id: 'sec-10', title: '10. Jurisdictional Governing Law' },
        { id: 'sec-11', title: '11. Corporate Communications' }
    ];

    const handleScrollTo = (id) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div
            className="position-relative py-5 min-vh-screen"
            style={{ backgroundColor: THEME.colors.secondary, color: THEME.colors.white, overflow: 'hidden' }}
        >
            <div
                className="position-absolute rounded-circle pointer-events-none"
                style={{
                    top: '-10%',
                    right: '5%',
                    width: '500px',
                    height: '500px',
                    background: `radial-gradient(circle, ${THEME.colors.primary}10 0%, transparent 70%)`,
                    filter: 'blur(80px)',
                    zIndex: 1
                }}
            />

            <Container className="position-relative py-5" style={{ zIndex: 2 }}>

                {/* Header Block */}
                <div className="border-bottom pb-4 mb-5" style={{ borderColor: customColors.borderMuted }}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div>
                            <h1 className="display-4 font-weight-bold text-white tracking-tight mb-2">Terms and Conditions</h1>
                            <p className="mb-0" style={{ color: THEME.colors.gray, fontWeight: '300', fontSize: '1.05rem' }}>
                                Welcome to Rasops. These Master Terms and Conditions govern your access to, integration with, and use of the Rasops engine, platforms, and auxiliary software components. Engaging with our services establishes full compliance with this framework.
                            </p>
                        </div>

                        <div
                            className="d-flex align-items-center gap-2 p-3 rounded-3"
                            style={{ backgroundColor: customColors.cardBg, border: `1px solid ${customColors.borderMuted}` }}
                        >
                            <FiClock style={{ color: THEME.colors.primary }} />
                            <small style={{ color: THEME.colors.textMuted, fontWeight: '500' }}>
                                Last Updated: <span className="text-white">24-Mar-2026</span>
                            </small>
                        </div>
                    </div>
                </div>

                <Row className="g-4">

                    {/* Sidebar */}
                    <Col lg={4} className="d-none d-lg-block position-sticky" style={{ top: '2rem', height: 'fit-content' }}>
                        <Card className="border-0 shadow-sm rounded-4" style={{ backgroundColor: customColors.cardBg }}>
                            <Card.Body className="p-3">
                                <p className="text-uppercase tracking-wider px-3 pt-2 font-weight-bold mb-3" style={{ color: THEME.colors.textMuted, fontSize: '0.75rem', letterSpacing: '1.5px' }}>
                                    Document Outline
                                </p>
                                <ListGroup variant="flush" className="bg-transparent border-0 rounded-3 overflow-hidden">
                                    {sections.map((sec) => {
                                        const isActive = activeSection === sec.id;
                                        return (
                                            <ListGroup.Item
                                                key={sec.id}
                                                action
                                                onClick={() => handleScrollTo(sec.id)}
                                                className="border-0 px-3 py-2 d-flex align-items-center justify-content-between text-start transition-all"
                                                style={{
                                                    backgroundColor: isActive ? `${THEME.colors.primary}12` : 'transparent',
                                                    color: isActive ? THEME.colors.primary : THEME.colors.gray,
                                                    fontSize: '0.85rem',
                                                    borderRadius: '8px',
                                                    marginBottom: '2px',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <span>{sec.title}</span>
                                                {isActive && <FiChevronRight size={16} />}
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Core Terms Text Container */}
                    <Col lg={8}>
                        <Card
                            className="border-0 p-4 p-md-5 shadow-lg rounded-4"
                            style={{ backgroundColor: customColors.cardBg, border: `1px solid ${customColors.borderMuted}`, lineHeight: '1.8' }}
                        >
                            <Card.Body className="p-0 text-white-50" style={{ fontSize: '0.95rem' }}>

                                <div id="sec-1" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">1. Corporate Verification & Eligibility</h3>
                                    <p>
                                        Rasops system operations are strictly licensed to verified commercial entities operating valid food service establishments. By deploying our infrastructure, you warrant that:
                                    </p>
                                    <ul className="ps-3">
                                        <li>Your corporate commercial entity is formally registered and active under prevailing corporate laws.</li>
                                        <li>You maintain all valid operations permits required to run a public culinary or food facility.</li>
                                        <li>You hold an active, valid license or certificate issued by the Food Safety and Standards Authority of India (FSSAI).</li>
                                        <li>You possess direct legal authorization and rights to govern system profiles and execute commercial configurations on Rasops.</li>
                                    </ul>
                                </div>

                                <div id="sec-2" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">2. Scope of Services</h3>
                                    <p>
                                        Rasops delivers an Integrated Cloud Restaurant Operating Space, providing digital menu structure controls, live automated order orchestration, integrated payment parsing links, and automated customer interaction interfaces.
                                    </p>
                                </div>

                                <div id="sec-3" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">3. Strategic User Responsibilities</h3>
                                    <p>Registrants utilizing the platform explicitly agree to:</p>
                                    <ul className="ps-3">
                                        <li>Ensure total operational precision across all published digital catalog items, configurations, and retail menus.</li>
                                        <li>Sustain robust encryption and secrecy practices across institutional credential keys.</li>
                                        <li>Execute all operational tasks in strict alignment with legal guidelines and food safety rules.</li>
                                        <li>Use software components exclusively for valid and verifiable commercial intents.</li>
                                    </ul>
                                </div>

                                <div id="sec-4" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">4. Financial Framework & Platform Fees</h3>
                                    <p>
                                        Rasops reserves the right to levy specific processing fees on processed transaction volumes. Financial clearing paths are run via integrated settlement nodes. Rasops disclaims all liabilities for transaction failures or technical drops caused by external financial providers.
                                    </p>
                                    <p className="mb-0">
                                        Merchant establishments assume exclusive operational accountability for processing reversals, processing cancellations, and resolving transaction balance disputes initiated by buyers.
                                    </p>
                                </div>

                                <div id="sec-5" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">5. Boundaries of Operational Liability</h3>
                                    <p>
                                        Rasops serves uniquely as an intermediary cloud engine architecture provider.
                                    </p>
                                    <ul className="ps-3">
                                        <li>Culinary quality, packaging safety, and raw processing remain under the exclusive liability of the merchant.</li>
                                        <li>Order fulfillment logistics, tracking execution, and fulfillment paths are run directly by the restaurant.</li>
                                        <li>Rasops bears no legal liability regarding transaction disputes or content conflicts between final buyers and culinary establishments.</li>
                                    </ul>
                                </div>

                                <div id="sec-6" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">6. Account Suspension & Service Termination</h3>
                                    <p>
                                        We reserve absolute discretionary rights to throttle access levels or indefinitely suspend corporate instances in occurrences involving:
                                    </p>
                                    <ul className="ps-3">
                                        <li>Direct structural breach of these master conditions.</li>
                                        <li>Malicious patterns or fraudulent transaction activities.</li>
                                        <li>Non-compliance with prevailing regulatory framework directives.</li>
                                    </ul>
                                </div>

                                <div id="sec-7" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">7. Proprietary Intellectual Property</h3>
                                    <p>
                                        All systemic rights linked with the Rasops framework—encompassing underlying cloud code, interface models, algorithmic calculations, and visualization frameworks—remain properties of Rasops. Unauthorized replication, framing, or distribution is prohibited.
                                    </p>
                                </div>

                                <div id="sec-8" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">8. Definite Limitation of Liability</h3>
                                    <p>
                                        Rasops disclaims all liability for secondary commercial damages, operational business interruptions, revenue loss, or file deletions resulting from platform use.
                                    </p>
                                </div>

                                <div id="sec-9" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">9. Revisions to Terms</h3>
                                    <p>
                                        We reserve the right to periodically alter these master rules. Persistent interaction with system systems following updates implies full agreement to the revised conditions.
                                    </p>
                                </div>

                                <div id="sec-10" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">10. Jurisdictional Governing Law</h3>
                                    <p>
                                        These corporate parameters shall be interpreted and enforced strictly under the sovereign legal frameworks of the Republic of India.
                                    </p>
                                </div>

                                <div id="sec-11" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">11. Corporate Communications</h3>
                                    <p>For administrative legal submissions or standard compliance coordination, connect directly with the operations desk:</p>

                                    <div
                                        className="d-flex align-items-center gap-3 p-3 rounded-3 mt-3"
                                        style={{ backgroundColor: THEME.colors.secondary, border: `1px solid ${customColors.borderMuted}` }}
                                    >
                                        <div className="p-2 rounded-2 d-flex align-items-center justify-content-center" style={{ backgroundColor: `${THEME.colors.primary}12`, color: THEME.colors.primary }}>
                                            <FiMail size={20} />
                                        </div>
                                        <div>
                                            <small className="d-block text-uppercase tracking-wider font-weight-bold" style={{ fontSize: '0.65rem', color: THEME.colors.textMuted }}>Corporate Legal Desk</small>
                                            <a href="mailto:rasops.info@gmail.com" className="font-weight-medium text-decoration-none" style={{ color: THEME.colors.white, transition: 'color 0.2s' }}>
                                                rasops.info@gmail.com
                                            </a>
                                        </div>
                                    </div>
                                </div>

                            </Card.Body>
                        </Card>
                    </Col>

                </Row>
            </Container>
        </div>
    );
}
